import { ProCard, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { App, Button, Checkbox, DatePicker, Flex, Form, Input, Popconfirm, Select, Spin, Tag, Typography } from 'antd';
import { get, isEqual, mapValues } from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { dRole, roles, uRole } from '@/apis/role';

/**
 * Components
 */
import ComponentCreateUpdate from './create_update';

/**
 * Types
 */
import type { QueryRoleDto, RoleDto } from '@/dtos/role.dto';
import type { ActionType, ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';
import type { Action } from './create_update';

/**
 * Constants
 */
import { RANGE_PICKER_PRESETS } from '@/constants/presets';

const defaultPayload: QueryRoleDto = {
    id: undefined,
    name_fuzzy: undefined,
    is_deleted: undefined,
    is_disabled: undefined,
    created_at: undefined,
};

const PageRoles: React.FC = () => {
    /**
     * Hooks
     */
    const { message } = App.useApp();

    const [formSearch] = Form.useForm();

    const tableRef = useRef<ActionType>();

    const updateRef = useRef<Action>();

    const { t } = useTranslation();

    /**
     * States
     */
    const [loading, setLoading] = useSetState<Record<string, boolean>>({});

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        showQuickJumper: true,
        showSizeChanger: true,
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
    });

    /**
     * Payloads
     */
    const [payload, setPayload] = useSetState<QueryRoleDto>({
        ...defaultPayload,
    });

    /**
     * Requests
     */
    const {
        data: dataRoles,
        isLoading: loadingRoles,
        refetch: refetchRoles,
    } = useQuery({
        queryKey: ['roles', pagination.current, pagination.pageSize, payload],
        retry: false,
        queryFn: () => roles({ page: pagination.current, size: pagination.pageSize, ...payload }),
    });

    /**
     * ChildrenProps
     */
    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: defaultPayload,
        layout: 'inline',
        onFinish: (values) => {
            values.created_at = [
                values.created_at?.[0].startOf('day').format(),
                values.created_at?.[1].endOf('day').format(),
            ];

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };

            if (isEqual(payload, assignPayload)) {
                return refetchRoles();
            }

            setPagination({
                current: 1,
            });

            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<RoleDto, QueryRoleDto> = {
        actionRef: tableRef,
        bordered: true,
        columns: [
            {
                dataIndex: 'name',
                title: t('name'),
            },
            {
                dataIndex: 'status',
                title: t('status'),
                width: 200,
                render: (_, entity) => {
                    if (entity.is_deleted) {
                        return (
                            <Tag bordered={false} color="red">
                                {t('status_deleted')}
                            </Tag>
                        );
                    }

                    return (
                        <>
                            {entity.is_disabled ? (
                                <Tag bordered={false} color="red">
                                    {t('status_disabled')}
                                </Tag>
                            ) : (
                                <Tag bordered={false} color="green">
                                    {t('status_enabled')}
                                </Tag>
                            )}
                        </>
                    );
                },
            },
            {
                dataIndex: 'created_at',
                title: t('created_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                dataIndex: 'updated_at',
                title: t('updated_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                fixed: 'right',
                key: 'option',
                title: t('actions'),
                valueType: 'option',
                width: 200,
                render: (_, entity) =>
                    entity.permissions.includes('super_admin')
                        ? '-'
                        : [
                              <a
                                  key="update"
                                  onClick={() => {
                                      updateRef.current?.update(entity);
                                  }}
                              >
                                  {t('edit')}
                              </a>,
                              <Spin key="delete" size="small" spinning={loading[entity.id] === true}>
                                  <Popconfirm
                                      title={t('ays')}
                                      onConfirm={() => {
                                          console.log('entity :>> ', entity);
                                          setLoading({ [entity.id]: true });

                                          (entity.is_deleted
                                              ? uRole({ id: entity.id, is_deleted: false, version: entity.version })
                                              : dRole(entity.id)
                                          )
                                              .then(
                                                  () => refetchRoles(),
                                                  (err) => message.error(err.msg),
                                              )
                                              .finally(() => {
                                                  setLoading({
                                                      [entity.id]: false,
                                                  });
                                              });
                                      }}
                                  >
                                      {entity.is_deleted ? (
                                          <Typography.Link type="success">{t('restore')}</Typography.Link>
                                      ) : (
                                          <Typography.Link type="danger">{t('delete')}</Typography.Link>
                                      )}
                                  </Popconfirm>
                              </Spin>,
                          ],
            },
        ],
        dataSource: get(dataRoles, 'data.data', []),
        loading: loadingRoles,
        options: {
            reload: () => refetchRoles(),
        },
        pagination: {
            ...pagination,
            total: get(dataRoles, 'data.meta.total', 0),
        },
        rowKey: 'id',
        scroll: {
            x: 1000,
        },
        search: false,
        toolbar: {
            actions: [<ComponentCreateUpdate key="create_update" ref={updateRef} onFinish={() => refetchRoles()} />],
        },
    };

    return (
        <>
            <ProCard className="mb-16">
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap="wrap">
                        <Form.Item name="id" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder="ID" style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="name_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('name')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="is_disabled" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                options={[
                                    { label: t('status_enabled'), value: false },
                                    { label: t('status_disabled'), value: true },
                                ]}
                                placeholder={t('status')}
                                style={{ width: 100 }}
                            />
                        </Form.Item>

                        <Form.Item label={t('created_at')} name="created_at" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear={true} presets={RANGE_PICKER_PRESETS(t)} />
                        </Form.Item>

                        <Form.Item name="is_deleted" style={{ marginRight: 0 }} valuePropName="checked">
                            <Checkbox>{t('status_deleted')}</Checkbox>
                        </Form.Item>

                        <Button
                            loading={loadingRoles}
                            type="primary"
                            onClick={() => {
                                formSearch.submit();
                            }}
                        >
                            {t('search')}
                        </Button>

                        <Button
                            onClick={() => {
                                formSearch.resetFields();
                            }}
                        >
                            {t('reset')}
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <ProTable {...proTableProps} />
        </>
    );
};

export default PageRoles;
